import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { formatUnits } from 'ethers';
import {
  Shield,
  Wallet,
  CheckCircle,
  AlertTriangle,
  PlusCircle,
  MinusCircle,
  Settings,
  List,
  Lock
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWeb3 } from '../contexts/Web3Context';
import api, { API_ENDPOINTS } from '../config/api';
import { formatWalletError, dismissWalletToasts } from '../utils/walletErrors';
import {
  DEPLOYER_WALLET_ADDRESS,
  JOB_CONTRACT_ADDRESS,
  WorkLobDAO_address,
  USDC_TOKEN_ABI,
  USDC_TOKEN_ADDRESS
} from '../config/Constants';
import {
  checkDeployer,
  getContractOwner,
  getJobFeeBasisPoints,
  setJobFeeBasisPoints,
  getContractBalance,
  addFundsToContract,
  withdrawFromContract
} from '../config/Deployerintegration';
import {
  fundPool,
  addToPool,
  withdrawFromPool,
  getPoolInfo,
  getLOBTokenBalance,
  checkTokenApproval,
  approveLOBTokens
} from '../config/StakingIntegration';
import {
  fetchDAOPoolBalances,
  waitForUSDCPoolBalance,
  getVoterRewardAmount,
  fundETHPool,
  withdrawETH,
  fundLOBTokenPool,
  withdrawLOBToken,
  setVoterRewardAmount
} from '../config/DAOIntegration';
import { WorkLobStaking_address } from '../config/Constants';

const formatPercent = (bps) => (bps / 100).toFixed(2);

const TransactionRow = ({ tx }) => {
  const explorerUrl = tx.txHash
    ? `https://basescan.org/tx/${tx.txHash}`
    : null;

  return (
    <tr className="border-b border-gray-100 dark:border-gray-800">
      <td className="px-4 py-3 text-sm font-medium text-heading capitalize">
        {tx.type.replace(/_/g, ' ')}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
        {tx.description}
      </td>
      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
        {tx.amount ? `${tx.amount} USD` : '--'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
        {new Date(tx.createdAt).toLocaleString()}
      </td>
      <td className="px-4 py-3 text-sm">
        {tx.txHash ? (
          <a
            href={explorerUrl}
            className="text-accent hover:underline"
            target="_blank"
            rel="noreferrer"
          >
            {tx.txHash.slice(0, 10)}...
          </a>
        ) : (
          '--'
        )}
      </td>
    </tr>
  );
};

const DeployerPortalPage = () => {
  const { user } = useAuth();
  const {
    isConnected,
    connectWallet,
    walletAddress,
    walletBalance,
    signer,
    provider,
    shortenAddress
  } = useWeb3();

  const [isVerified, setIsVerified] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [jobFeeBps, setJobFeeBps] = useState(null);
  const [feeInput, setFeeInput] = useState('');
  const [balanceWei, setBalanceWei] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [addAmount, setAddAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [ethPriceUsd, setEthPriceUsd] = useState(null);
  const [usdcDecimals, setUsdcDecimals] = useState(6);
  const [loggingDisabled, setLoggingDisabled] = useState(false);
  const [contractOwner, setContractOwner] = useState('');
  const [ownerMismatch, setOwnerMismatch] = useState(false);
  
  // Staking pool management
  const [poolInfo, setPoolInfo] = useState(null);
  const [poolRewardAmount, setPoolRewardAmount] = useState('');
  const [poolDuration, setPoolDuration] = useState('');
  const [poolAddAmount, setPoolAddAmount] = useState('');
  const [poolWithdrawAmount, setPoolWithdrawAmount] = useState('');
  const [lobTokenBalance, setLobTokenBalance] = useState('0');
  const [isPoolLoading, setIsPoolLoading] = useState(false);
  
  // DAO pool management
  const [daoETHPoolBalance, setDaoETHPoolBalance] = useState('0');
  const [daoLOBTokenPoolBalance, setDaoLOBTokenPoolBalance] = useState('0');
  const [voterRewardAmount, setVoterRewardAmount] = useState('0');
  const [daoETHFundAmount, setDaoETHFundAmount] = useState('');
  const [daoETHWithdrawAmount, setDaoETHWithdrawAmount] = useState('');
  const [daoETHWithdrawAddress, setDaoETHWithdrawAddress] = useState('');
  const [daoLOBTokenFundAmount, setDaoLOBTokenFundAmount] = useState('');
  const [daoLOBTokenWithdrawAmount, setDaoLOBTokenWithdrawAmount] = useState('');
  const [daoLOBTokenWithdrawAddress, setDaoLOBTokenWithdrawAddress] = useState('');
  const [voterRewardInput, setVoterRewardInput] = useState('');
  const [isDAOLoading, setIsDAOLoading] = useState(false);
  const [daoPoolBalanceLoading, setDaoPoolBalanceLoading] = useState(false);
  const [daoPoolBalanceError, setDaoPoolBalanceError] = useState(null);
  
  // Configuration management
  const [config, setConfig] = useState({
    activity_points_job_completion: 10,
    activity_points_voting: 5,
    min_locked_staking_governance: 100,
    min_activity_points_governance: 20,
    voter_reward_amount: 0,
    settlement_percentage: 90,
    voting_duration_days: 5,
    blog_earnings_views_rate: 100,
    blog_earnings_views_threshold: 1000,
    blog_earnings_impressions_rate: 100,
    blog_earnings_impressions_threshold: 100
  });
  const [configInputs, setConfigInputs] = useState({
    activity_points_job_completion: '10',
    activity_points_voting: '5',
    min_locked_staking_governance: '100',
    min_activity_points_governance: '20',
    voter_reward_amount: '0',
    settlement_percentage: '90',
    voting_duration_days: '5',
    blog_earnings_views_rate: '100',
    blog_earnings_views_threshold: '1000',
    blog_earnings_impressions_rate: '100',
    blog_earnings_impressions_threshold: '100'
  });
  const [isConfigLoading, setIsConfigLoading] = useState(false);
  const [isUpdatingConfig, setIsUpdatingConfig] = useState(false);
  const [isEscrowLoading, setIsEscrowLoading] = useState(false);

  const formatErrorMessage = (error, fallback) => formatWalletError(error, fallback);

  const isDeployerUser = useMemo(() => {
    if (!DEPLOYER_WALLET_ADDRESS || !user?.walletAddress) return false;
    return (
      user.walletAddress.toLowerCase() === DEPLOYER_WALLET_ADDRESS.toLowerCase()
    );
  }, [user?.walletAddress]);

  useEffect(() => {
    if (walletAddress) {
      setWithdrawAddress(walletAddress);
    }
  }, [walletAddress]);

  const fetchTransactions = async ({ queryKey }) => {
    const [, page] = queryKey;
    const response = await api.get(API_ENDPOINTS.DEPLOYER.TRANSACTIONS, {
      params: { page, limit: 20 }
    });
    return response.data;
  };

  const {
    data: transactionsData,
    isLoading: transactionsLoading,
    refetch: refetchTransactions
  } = useQuery(
    ['deployerTransactions', transactionsPage],
    fetchTransactions,
    {
      enabled: isDeployerUser && isVerified && !loggingDisabled,
      keepPreviousData: true,
      onError: (error) => {
        console.error('Failed to load deployer transactions', error);
        if (error.response?.status === 503) {
          setLoggingDisabled(true);
          toast.error(
            'Server deployer wallet is not configured. Transactions cannot be recorded.'
          );
        }
      }
    }
  );

  const logDeployerAction = async ({
    type,
    amount = 0,
    description,
    txHash,
    direction = 'debit',
    metadata = {}
  }) => {
    if (loggingDisabled) return;
    try {
      await api.post(API_ENDPOINTS.DEPLOYER.LOG_TRANSACTION, {
        type,
        amount,
        description,
        txHash,
        direction,
        metadata: {
          ...metadata,
          contractAddress: JOB_CONTRACT_ADDRESS
        }
      });
      await refetchTransactions();
    } catch (error) {
      console.error('Failed to log deployer action', error);
      setLoggingDisabled(true);
      toast.error(
        error.response?.data?.error ||
          'Deployer logging unavailable. Check backend configuration.'
      );
    }
  };

  const resolveCurrentSigner = async () => {
    if (signer) return signer;
    if (provider?.getSigner) {
      return provider.getSigner();
    }
    throw new Error('Wallet signer not available');
  };

  const ensureOwnerSigner = async () => {
    const currentSigner = await resolveCurrentSigner();
    const chainSource = provider || currentSigner;
    const owner = (await getContractOwner(chainSource)).toLowerCase();
    const signerAddress = (await currentSigner.getAddress()).toLowerCase();

    setContractOwner(owner);
    setOwnerMismatch(owner !== signerAddress);

    if (owner !== signerAddress) {
      throw new Error(
        `Connected wallet is not the contract owner. Please switch to ${owner}.`
      );
    }

    return currentSigner;
  };

  const loadFeeAndBalance = async (retryCount = 0) => {
    if (!provider || !isVerified) return;
    setBalanceLoading(true);
    try {
      const currentSigner = await ensureOwnerSigner();
      const [fee, balance] = await Promise.all([
        getJobFeeBasisPoints(provider),
        getContractBalance(currentSigner)
      ]);
      setJobFeeBps(fee);
      setFeeInput((fee / 100).toString());
      setBalanceWei(balance);
      console.log('Balance refreshed:', formatUnits(balance, usdcDecimals), 'USDC');
    } catch (error) {
      console.error('Failed to load contract data', error);
      // Retry once if it fails (might be a temporary network issue)
      if (retryCount < 1) {
        console.log('Retrying balance load...');
        setTimeout(() => loadFeeAndBalance(retryCount + 1), 2000);
        return;
      }
      // Only show error toast if not a retry
      if (retryCount === 0) {
        toast.error(
          formatErrorMessage(
            error,
            'Unable to load contract data. Ensure you are connected with the deployer wallet.'
          )
        );
      }
    } finally {
      setBalanceLoading(false);
    }
  };

  useEffect(() => {
    if (isVerified) {
      loadFeeAndBalance();
    }
  }, [isVerified, signer, provider]);

  useEffect(() => {
    const fetchUsdcDecimals = async () => {
      try {
        const source = provider || signer;
        if (!source) return;
        const token = new (await import('ethers')).Contract(
          USDC_TOKEN_ADDRESS,
          USDC_TOKEN_ABI,
          source
        );
        const d = await token.decimals();
        setUsdcDecimals(Number(d));
      } catch (e) {
        setUsdcDecimals(6);
      }
    };
    fetchUsdcDecimals();
  }, [provider, signer]);

  useEffect(() => {
    const fetchOwner = async () => {
      const source = provider || signer;
      if (!source) return;
      try {
        const owner = (await getContractOwner(source)).toLowerCase();
        setContractOwner(owner);
        if (walletAddress) {
          setOwnerMismatch(owner !== walletAddress.toLowerCase());
        }
      } catch (error) {
        console.warn('Unable to fetch contract owner', error);
      }
    };
    fetchOwner();
  }, [provider, signer, walletAddress]);

  useEffect(() => {
    const fetchPrice = async () => {
      try {
        const { data } = await api.get('/v1/price/usdc');
        if (data?.usd) setEthPriceUsd(Number(data.usd));
      } catch (error) {
        console.error('Failed to fetch USDC price', error);
      }
    };
    fetchPrice();
    const interval = setInterval(fetchPrice, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const storedFlag = localStorage.getItem('deployerVerified') === 'true';
    const storedWallet = localStorage.getItem('deployerVerifiedWallet');

    if (!walletAddress) {
      localStorage.removeItem('deployerVerified');
      localStorage.removeItem('deployerVerifiedWallet');
      setIsVerified(false);
      return;
    }

    if (
      storedFlag &&
      storedWallet &&
      storedWallet === walletAddress.toLowerCase()
    ) {
      setIsVerified(true);
    } else {
      setIsVerified(false);
    }
  }, [walletAddress]);

  const canUseOwnerActions = isVerified && !ownerMismatch;

  const handleVerify = async () => {
    if (!isConnected) {
      const result = await connectWallet();
      if (!result?.address) {
        toast.error('Wallet connection is required');
        return;
      }
    }

    setIsVerifying(true);
    try {
      const currentSigner = await ensureOwnerSigner();
      await checkDeployer(currentSigner);
      setIsVerified(true);
      localStorage.setItem('deployerVerified', 'true');
      if (walletAddress) {
        localStorage.setItem(
          'deployerVerifiedWallet',
          walletAddress.toLowerCase()
        );
      }
      toast.success('Wallet verified as deployer');
      await logDeployerAction({
        type: 'deployer_verify',
        description: 'Verified deployer access',
        direction: 'credit'
      });
      await loadFeeAndBalance();
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Verification failed');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleUpdateFee = async () => {
    if (!isVerified) {
      toast.error('Verify deployer wallet first');
      return;
    }
    if (ownerMismatch) {
      toast.error('Please connect the contract owner wallet');
      return;
    }

    const percent = Number(feeInput);
    if (Number.isNaN(percent) || percent < 0 || percent > 20) {
      toast.error('Fee must be between 0% and 20%');
      return;
    }

    const feeBasisPoints = Math.round(percent * 100);
    setIsEscrowLoading(true);
    const loadingToast = toast.loading('Updating job fee...');
    try {
      const currentSigner = await ensureOwnerSigner();
      const tx = await setJobFeeBasisPoints({
        signer: currentSigner,
        feeBasisPoints
      });
      toast.dismiss(loadingToast);
      toast.success('Job fee updated');
      await logDeployerAction({
        type: 'deployer_set_fee',
        description: `Set job fee to ${percent.toFixed(2)}%`,
        txHash: tx.hash,
        direction: 'credit',
        metadata: { feeBasisPoints }
      });
      await loadFeeAndBalance();
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error(error);
      toast.dismiss(loadingToast);
      toast.error(
        formatErrorMessage(
          error,
          'Failed to update job fee. Ensure you are connected with the deployer wallet.'
        )
      );
    } finally {
      dismissWalletToasts();
      setIsEscrowLoading(false);
    }
  };

  const handleAddFunds = async () => {
    if (!isVerified) {
      toast.error('Verify deployer wallet first');
      return;
    }
    if (ownerMismatch) {
      toast.error('Please connect the contract owner wallet');
      return;
    }
    if (!addAmount || Number(addAmount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setIsEscrowLoading(true);
    const loadingToast = toast.loading('Adding funds to escrow...');
    try {
      const currentSigner = await ensureOwnerSigner();
      const tx = await addFundsToContract({
        signer: currentSigner,
        amountUsdc: addAmount
      });

      toast.dismiss(loadingToast);
      toast.success('Transaction submitted. Waiting for confirmation...');
      await tx.wait();

      toast.success('Funds added to contract');
      await logDeployerAction({
        type: 'deployer_add_funds',
        amount: Number(addAmount),
        description: `Added ${addAmount} USDC to escrow`,
        txHash: tx.hash,
        metadata: { amountUsdc: addAmount }
      });
      setAddAmount('');
      
      // Refresh balance after transaction is confirmed
      // Wait a bit for blockchain state to update, then refresh
      await new Promise(resolve => setTimeout(resolve, 2000));
      await loadFeeAndBalance();
      // Refresh page after successful transaction
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error(error);
      toast.dismiss(loadingToast);
      const formatted = formatErrorMessage(
        error,
        'Failed to add funds. Ensure you are connected with the deployer wallet.'
      );
      if (formatted.toLowerCase().includes('insufficient')) {
        toast.error(
          'Wallet does not have enough ETH for gas or enough USDC for this amount.'
        );
      } else {
        toast.error(formatted);
      }
    } finally {
      dismissWalletToasts();
      setIsEscrowLoading(false);
    }
  };

  const handleWithdraw = async () => {
    if (!isVerified) {
      toast.error('Verify deployer wallet first');
      return;
    }
    if (ownerMismatch) {
      toast.error('Please connect the contract owner wallet');
      return;
    }
    if (!withdrawAmount || Number(withdrawAmount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    if (!withdrawAddress) {
      toast.error('Destination address is required');
      return;
    }
    
    // Validate withdrawal amount against contract balance
    const withdrawAmountNum = Number(withdrawAmount);
    if (balanceEth !== null && withdrawAmountNum > balanceEth) {
      toast.error(
        `Insufficient escrow balance. Available: ${balanceEth.toFixed(2)} USDC, Requested: ${withdrawAmount} USDC`
      );
      return;
    }

    setIsEscrowLoading(true);
    const loadingToast = toast.loading('Withdrawing from escrow...');
    try {
      const currentSigner = await ensureOwnerSigner();
      const tx = await withdrawFromContract({
        signer: currentSigner,
        to: withdrawAddress,
        amountUsdc: withdrawAmount
      });

      toast.dismiss(loadingToast);
      toast.success('Transaction submitted. Waiting for confirmation...');
      await tx.wait();

      toast.success('Withdrawal successful');
      await logDeployerAction({
        type: 'deployer_withdraw',
        amount: Number(withdrawAmount),
        description: `Withdrew ${withdrawAmount} USDC from escrow to ${withdrawAddress}`,
        txHash: tx.hash,
        direction: 'credit',
        metadata: { toAddress: withdrawAddress }
      });
      setWithdrawAmount('');
      
      // Refresh balance after transaction is confirmed
      // Wait a bit for blockchain state to update, then refresh
      await new Promise(resolve => setTimeout(resolve, 2000));
      await loadFeeAndBalance();
      // Refresh page after successful transaction
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error(error);
      toast.dismiss(loadingToast);
      const formatted = formatErrorMessage(
        error,
        'Failed to withdraw funds. Ensure you are using the deployer wallet.'
      );
      if (formatted.toLowerCase().includes('insufficient balance')) {
        toast.error(
          'Contract balance is insufficient for this withdrawal. Add funds first or reduce the amount.'
        );
      } else {
        toast.error(formatted);
      }
    } finally {
      dismissWalletToasts();
      setIsEscrowLoading(false);
    }
  };

  const balanceEth = balanceWei ? Number(formatUnits(balanceWei, usdcDecimals)) : null;
  const balanceUsd = balanceEth !== null ? balanceEth.toFixed(2) : null;

  // Load pool info
  useEffect(() => {
    const loadPoolInfo = async () => {
      if (!provider || !isVerified) return;
      try {
        const info = await getPoolInfo(provider);
        setPoolInfo(info);
      } catch (error) {
        console.error('Error loading pool info:', error);
      }
    };
    loadPoolInfo();
    const interval = setInterval(loadPoolInfo, 30000);
    return () => clearInterval(interval);
  }, [provider, isVerified]);

  // Load LOB token balance
  useEffect(() => {
    const loadLOBBalance = async () => {
      if (!isConnected || !walletAddress) {
        setLobTokenBalance('0');
        return;
      }

      // Use provider if available, otherwise use signer
      const balanceProvider = provider || signer;
      if (!balanceProvider) {
        setLobTokenBalance('0');
        return;
      }

      try {
        const balance = await getLOBTokenBalance(balanceProvider, walletAddress);
        setLobTokenBalance(balance || '0');
      } catch (error) {
        console.error('Error loading LOB balance:', error);
        setLobTokenBalance('0');
      }
    };
    loadLOBBalance();
    const interval = setInterval(loadLOBBalance, 30000);
    return () => clearInterval(interval);
  }, [isConnected, walletAddress, provider, signer]);

  const refreshDAOPoolBalances = async () => {
    if (!isDeployerUser || !WorkLobDAO_address) {
      return;
    }
    setDaoPoolBalanceLoading(true);
    setDaoPoolBalanceError(null);
    try {
      const { usdcPoolBalance, lobPoolBalance } = await fetchDAOPoolBalances();
      setDaoETHPoolBalance(usdcPoolBalance || '0');
      setDaoLOBTokenPoolBalance(lobPoolBalance || '0');
    } catch (error) {
      console.error('Error loading DAO pool balances from chain:', error);
      setDaoPoolBalanceError(
        formatErrorMessage(error, 'Could not load DAO pool balances from chain')
      );
    } finally {
      setDaoPoolBalanceLoading(false);
    }
  };

  // Load DAO pool balances from Base mainnet (not wallet provider — survives refresh)
  useEffect(() => {
    if (!isDeployerUser) return;
    refreshDAOPoolBalances();
    const interval = setInterval(refreshDAOPoolBalances, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeployerUser, isVerified]);

  useEffect(() => {
    if (walletAddress) {
      setDaoETHWithdrawAddress(walletAddress);
      setDaoLOBTokenWithdrawAddress(walletAddress);
    }
  }, [walletAddress]);

  // Load configuration
  useEffect(() => {
    const loadConfig = async () => {
      if (!isDeployerUser || !isVerified) return;
      setIsConfigLoading(true);
      try {
        const { data } = await api.get(API_ENDPOINTS.DEPLOYER.CONFIG);
        setConfig(data);
        setConfigInputs({
          activity_points_job_completion: data.activity_points_job_completion?.toString() || '10',
          activity_points_voting: data.activity_points_voting?.toString() || '5',
          min_locked_staking_governance: data.min_locked_staking_governance?.toString() || '100',
          min_activity_points_governance: data.min_activity_points_governance?.toString() || '20',
          voter_reward_amount: data.voter_reward_amount?.toString() || '0',
          settlement_percentage: data.settlement_percentage?.toString() || '90',
          voting_duration_days: data.voting_duration_days?.toString() || '5',
          blog_earnings_views_rate: data.blog_earnings_views_rate?.toString() || '100',
          blog_earnings_views_threshold: data.blog_earnings_views_threshold?.toString() || '1000',
          blog_earnings_impressions_rate: data.blog_earnings_impressions_rate?.toString() || '100',
          blog_earnings_impressions_threshold: data.blog_earnings_impressions_threshold?.toString() || '100'
        });
        // Also set the voter reward amount state from config
        setVoterRewardAmount(data.voter_reward_amount?.toString() || '0');
      } catch (error) {
        console.error('Error loading config:', error);
        toast.error('Failed to load configuration');
      } finally {
        setIsConfigLoading(false);
      }
    };
    loadConfig();
  }, [isDeployerUser, isVerified]);

  // Update configuration
  const handleUpdateConfig = async () => {
    if (!isDeployerUser || !isVerified) {
      toast.error('You must be verified as deployer to update configuration');
      return;
    }

    setIsUpdatingConfig(true);
    try {
      const updateData = {};
      if (configInputs.activity_points_job_completion !== undefined) {
        const value = parseInt(configInputs.activity_points_job_completion);
        if (isNaN(value) || value < 0) {
          toast.error('Activity points for job completion must be a non-negative number');
          setIsUpdatingConfig(false);
          return;
        }
        updateData.activity_points_job_completion = value;
      }
      if (configInputs.activity_points_voting !== undefined) {
        const value = parseInt(configInputs.activity_points_voting);
        if (isNaN(value) || value < 0) {
          toast.error('Activity points for voting must be a non-negative number');
          setIsUpdatingConfig(false);
          return;
        }
        updateData.activity_points_voting = value;
      }
      if (configInputs.min_locked_staking_governance !== undefined) {
        const value = parseInt(configInputs.min_locked_staking_governance);
        if (isNaN(value) || value < 0) {
          toast.error('Minimum locked staking must be a non-negative number');
          setIsUpdatingConfig(false);
          return;
        }
        updateData.min_locked_staking_governance = value;
      }
      if (configInputs.min_activity_points_governance !== undefined) {
        const value = parseInt(configInputs.min_activity_points_governance);
        if (isNaN(value) || value < 0) {
          toast.error('Minimum activity points for governance must be a non-negative number');
          setIsUpdatingConfig(false);
          return;
        }
        updateData.min_activity_points_governance = value;
      }
      if (configInputs.settlement_percentage !== undefined) {
        const value = parseFloat(configInputs.settlement_percentage);
        if (isNaN(value) || value < 0 || value > 100) {
          toast.error('Settlement percentage must be a number between 0 and 100');
          setIsUpdatingConfig(false);
          return;
        }
        updateData.settlement_percentage = value;
      }
      if (configInputs.voting_duration_days !== undefined) {
        const value = parseInt(configInputs.voting_duration_days);
        if (isNaN(value) || value < 1) {
          toast.error('Voting duration days must be a positive number');
          setIsUpdatingConfig(false);
          return;
        }
        updateData.voting_duration_days = value;
      }
      if (configInputs.blog_earnings_views_rate !== undefined) {
        const value = parseInt(configInputs.blog_earnings_views_rate);
        if (isNaN(value) || value < 0) {
          toast.error('Blog earnings views rate must be a non-negative number');
          setIsUpdatingConfig(false);
          return;
        }
        updateData.blog_earnings_views_rate = value;
      }
      if (configInputs.blog_earnings_views_threshold !== undefined) {
        const value = parseInt(configInputs.blog_earnings_views_threshold);
        if (isNaN(value) || value < 1) {
          toast.error('Blog earnings views threshold must be a positive number');
          setIsUpdatingConfig(false);
          return;
        }
        updateData.blog_earnings_views_threshold = value;
      }
      if (configInputs.blog_earnings_impressions_rate !== undefined) {
        const value = parseInt(configInputs.blog_earnings_impressions_rate);
        if (isNaN(value) || value < 0) {
          toast.error('Blog earnings impressions rate must be a non-negative number');
          setIsUpdatingConfig(false);
          return;
        }
        updateData.blog_earnings_impressions_rate = value;
      }
      if (configInputs.blog_earnings_impressions_threshold !== undefined) {
        const value = parseInt(configInputs.blog_earnings_impressions_threshold);
        if (isNaN(value) || value < 1) {
          toast.error('Blog earnings impressions threshold must be a positive number');
          setIsUpdatingConfig(false);
          return;
        }
        updateData.blog_earnings_impressions_threshold = value;
      }

      const { data } = await api.put(API_ENDPOINTS.DEPLOYER.UPDATE_CONFIG, updateData);
      setConfig(data.config);
      setConfigInputs({
        activity_points_job_completion: data.config.activity_points_job_completion?.toString() || '10',
        activity_points_voting: data.config.activity_points_voting?.toString() || '5',
        min_locked_staking_governance: data.config.min_locked_staking_governance?.toString() || '100',
        min_activity_points_governance: data.config.min_activity_points_governance?.toString() || '20',
        voter_reward_amount: data.config.voter_reward_amount?.toString() || '0',
        settlement_percentage: data.config.settlement_percentage?.toString() || '90',
        voting_duration_days: data.config.voting_duration_days?.toString() || '5',
        blog_earnings_views_rate: data.config.blog_earnings_views_rate?.toString() || '100',
        blog_earnings_views_threshold: data.config.blog_earnings_views_threshold?.toString() || '1000',
        blog_earnings_impressions_rate: data.config.blog_earnings_impressions_rate?.toString() || '100',
        blog_earnings_impressions_threshold: data.config.blog_earnings_impressions_threshold?.toString() || '100'
      });
      toast.success('Configuration updated successfully');
      window.location.reload();
    } catch (error) {
      console.error('Error updating config:', error);
      toast.error(error.response?.data?.error || 'Failed to update configuration');
    } finally {
      setIsUpdatingConfig(false);
    }
  };

  // Fund pool
  const handleFundPool = async () => {
    if (!poolRewardAmount || !poolDuration) {
      toast.error('Please enter reward amount and duration');
      return;
    }

    const amount = parseFloat(poolRewardAmount);
    const balance = parseFloat(lobTokenBalance);

    if (amount > balance) {
      toast.error('Insufficient LOB token balance');
      return;
    }

    setIsPoolLoading(true);
    try {
      const signer = await ensureOwnerSigner();
      
      // Check and approve tokens first
      toast.loading('Approving tokens...');
      const approved = await checkTokenApproval(signer, poolRewardAmount);
      if (!approved) {
        await approveLOBTokens(signer, poolRewardAmount);
        toast.dismiss();
        toast.success('Tokens approved');
      } else {
        toast.dismiss();
      }
      
      toast.loading('Funding staking pool...');
      const durationSeconds = parseInt(poolDuration) * 24 * 60 * 60; // Convert days to seconds
      const result = await fundPool(signer, poolRewardAmount, durationSeconds);
      
      toast.dismiss();
      toast.success('Pool funded successfully!');
      
      // Log transaction
      await logDeployerAction({
        type: 'staking_pool_fund',
        amount: Number(poolRewardAmount),
        description: `Funded staking pool with ${poolRewardAmount} LOB for ${poolDuration} days`,
        txHash: result.txHash,
        direction: 'debit',
        metadata: {
          rewardAmount: poolRewardAmount,
          duration: poolDuration,
          durationSeconds: durationSeconds
        }
      });
      
      setPoolRewardAmount('');
      setPoolDuration('');
      
      // Refresh pool info and balance
      const [info, newBalance] = await Promise.all([
        getPoolInfo(provider),
        getLOBTokenBalance(provider || signer, walletAddress)
      ]);
      setPoolInfo(info);
      setLobTokenBalance(newBalance || '0');
      // Refresh page after successful transaction
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      toast.dismiss();
      const formatted = formatErrorMessage(error, 'Failed to fund pool');
      toast.error(formatted);
    } finally {
      dismissWalletToasts();
      setIsPoolLoading(false);
    }
  };

  // Add to pool
  const handleAddToPool = async () => {
    if (!poolAddAmount) {
      toast.error('Please enter amount');
      return;
    }

    const amount = parseFloat(poolAddAmount);
    const balance = parseFloat(lobTokenBalance);

    if (amount > balance) {
      toast.error('Insufficient LOB token balance');
      return;
    }

    setIsPoolLoading(true);
    try {
      const signer = await ensureOwnerSigner();
      
      // Check and approve tokens first
      toast.loading('Approving tokens...');
      const approved = await checkTokenApproval(signer, poolAddAmount);
      if (!approved) {
        await approveLOBTokens(signer, poolAddAmount);
        toast.dismiss();
        toast.success('Tokens approved');
      } else {
        toast.dismiss();
      }
      
      toast.loading('Adding tokens to pool...');
      const result = await addToPool(signer, poolAddAmount);
      
      toast.dismiss();
      toast.success('Tokens added to pool successfully!');
      
      // Log transaction
      await logDeployerAction({
        type: 'staking_pool_add',
        amount: Number(poolAddAmount),
        description: `Added ${poolAddAmount} LOB to staking pool`,
        txHash: result.txHash,
        direction: 'debit',
        metadata: {
          amount: poolAddAmount
        }
      });
      
      setPoolAddAmount('');
      
      // Refresh pool info and balance
      const [info, newBalance] = await Promise.all([
        getPoolInfo(provider),
        getLOBTokenBalance(provider || signer, walletAddress)
      ]);
      setPoolInfo(info);
      setLobTokenBalance(newBalance || '0');
      // Refresh page after successful transaction
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      toast.dismiss();
      const formatted = formatErrorMessage(error, 'Failed to add to pool');
      toast.error(formatted);
    } finally {
      dismissWalletToasts();
      setIsPoolLoading(false);
    }
  };

  // Withdraw from pool
  const handleWithdrawFromPool = async () => {
    if (!poolWithdrawAmount) {
      toast.error('Please enter amount');
      return;
    }

    setIsPoolLoading(true);
    try {
      const signer = await ensureOwnerSigner();
      toast.loading('Withdrawing from pool...');
      
      const result = await withdrawFromPool(signer, poolWithdrawAmount);
      
      toast.dismiss();
      toast.success('Tokens withdrawn from pool successfully!');
      
      // Log transaction
      await logDeployerAction({
        type: 'staking_pool_withdraw',
        amount: Number(poolWithdrawAmount),
        description: `Withdrew ${poolWithdrawAmount} LOB from staking pool`,
        txHash: result.txHash,
        direction: 'credit',
        metadata: {
          amount: poolWithdrawAmount
        }
      });
      
      setPoolWithdrawAmount('');
      
      // Refresh pool info and balance
      const [info, newBalance] = await Promise.all([
        getPoolInfo(provider),
        getLOBTokenBalance(provider || signer, walletAddress)
      ]);
      setPoolInfo(info);
      setLobTokenBalance(newBalance || '0');
      // Refresh page after successful transaction
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      toast.dismiss();
      const formatted = formatErrorMessage(error, 'Failed to withdraw from pool');
      toast.error(formatted);
    } finally {
      dismissWalletToasts();
      setIsPoolLoading(false);
    }
  };

  // DAO Pool Handlers
  const handleFundETHPool = async () => {
    if (!daoETHFundAmount || parseFloat(daoETHFundAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    const fundAmt = parseFloat(daoETHFundAmount);
    const walletUsdc = parseFloat(walletBalance || '0');
    if (!Number.isNaN(fundAmt) && !Number.isNaN(walletUsdc) && fundAmt > walletUsdc) {
      toast.error(
        `Insufficient USDC in connected wallet. Need ${fundAmt} USDC, have ${walletUsdc.toFixed(4)} USDC.`
      );
      return;
    }

    setIsDAOLoading(true);
    try {
      const signer = await ensureOwnerSigner();
      toast.loading('Funding USDC pool...');
      const result = await fundETHPool(signer, daoETHFundAmount);
      toast.dismiss();
      toast.success('USDC pool funded successfully!');
      
      await logDeployerAction({
        type: 'dao_pool_fund_eth',
        amount: Number(daoETHFundAmount),
        description: `Funded DAO USDC pool with ${daoETHFundAmount} USDC`,
        txHash: result.txHash,
        direction: 'debit',
        metadata: { poolType: 'USDC', amount: daoETHFundAmount }
      });
      
      setDaoETHFundAmount('');
      await loadFeeAndBalance();
      const usdcPool = await waitForUSDCPoolBalance({
        minAmount: 0,
      });
      setDaoETHPoolBalance(usdcPool || '0');
      await refreshDAOPoolBalances();
    } catch (error) {
      toast.dismiss();
      toast.error(formatErrorMessage(error, 'Failed to fund USDC pool'));
    } finally {
      dismissWalletToasts();
      setIsDAOLoading(false);
    }
  };

  const handleWithdrawETHPool = async () => {
    if (!daoETHWithdrawAmount || !daoETHWithdrawAddress) {
      toast.error('Please enter amount and address');
      return;
    }

    setIsDAOLoading(true);
    try {
      const signer = await ensureOwnerSigner();
      toast.loading('Withdrawing from USDC pool...');
      const result = await withdrawETH(signer, daoETHWithdrawAddress, daoETHWithdrawAmount);
      toast.dismiss();
      toast.success('USDC withdrawn from pool successfully!');
      
      await logDeployerAction({
        type: 'dao_pool_withdraw_eth',
        amount: Number(daoETHWithdrawAmount),
        description: `Withdrew ${daoETHWithdrawAmount} USDC from DAO pool to ${daoETHWithdrawAddress}`,
        txHash: result.txHash,
        direction: 'credit',
        metadata: { poolType: 'USDC', toAddress: daoETHWithdrawAddress, amount: daoETHWithdrawAmount }
      });
      
      setDaoETHWithdrawAmount('');
      await refreshDAOPoolBalances();
      await loadFeeAndBalance();
    } catch (error) {
      toast.dismiss();
      toast.error(formatErrorMessage(error, 'Failed to withdraw from USDC pool'));
    } finally {
      dismissWalletToasts();
      setIsDAOLoading(false);
    }
  };

  const handleFundLOBTokenPool = async () => {
    if (!daoLOBTokenFundAmount || parseFloat(daoLOBTokenFundAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    const amount = parseFloat(daoLOBTokenFundAmount);
    const balance = parseFloat(lobTokenBalance);

    if (amount > balance) {
      toast.error('Insufficient LOB token balance');
      return;
    }

    setIsDAOLoading(true);
    try {
      const signer = await ensureOwnerSigner();
      toast.loading('Funding LOB token pool...');
      const result = await fundLOBTokenPool(signer, daoLOBTokenFundAmount);
      toast.dismiss();
      toast.success('LOB token pool funded successfully!');
      
      await logDeployerAction({
        type: 'dao_pool_fund_lob',
        amount: Number(daoLOBTokenFundAmount),
        description: `Funded DAO LOB token pool with ${daoLOBTokenFundAmount} LOB`,
        txHash: result.txHash,
        direction: 'debit',
        metadata: { poolType: 'LOB', amount: daoLOBTokenFundAmount }
      });
      
      setDaoLOBTokenFundAmount('');
      const newUserBalance = await getLOBTokenBalance(provider || signer, walletAddress);
      setLobTokenBalance(newUserBalance || '0');
      await refreshDAOPoolBalances();
    } catch (error) {
      toast.dismiss();
      toast.error(formatErrorMessage(error, 'Failed to fund LOB token pool'));
    } finally {
      dismissWalletToasts();
      setIsDAOLoading(false);
    }
  };

  const handleWithdrawLOBTokenPool = async () => {
    if (!daoLOBTokenWithdrawAmount || !daoLOBTokenWithdrawAddress) {
      toast.error('Please enter amount and address');
      return;
    }

    setIsDAOLoading(true);
    try {
      const signer = await ensureOwnerSigner();
      toast.loading('Withdrawing from LOB token pool...');
      const result = await withdrawLOBToken(signer, daoLOBTokenWithdrawAddress, daoLOBTokenWithdrawAmount);
      toast.dismiss();
      toast.success('LOB tokens withdrawn from pool successfully!');
      
      await logDeployerAction({
        type: 'dao_pool_withdraw_lob',
        amount: Number(daoLOBTokenWithdrawAmount),
        description: `Withdrew ${daoLOBTokenWithdrawAmount} LOB from DAO pool to ${daoLOBTokenWithdrawAddress}`,
        txHash: result.txHash,
        direction: 'credit',
        metadata: { poolType: 'LOB', toAddress: daoLOBTokenWithdrawAddress, amount: daoLOBTokenWithdrawAmount }
      });
      
      setDaoLOBTokenWithdrawAmount('');
      await refreshDAOPoolBalances();
    } catch (error) {
      toast.dismiss();
      toast.error(formatErrorMessage(error, 'Failed to withdraw from LOB token pool'));
    } finally {
      dismissWalletToasts();
      setIsDAOLoading(false);
    }
  };

  const handleSetVoterRewardAmount = async () => {
    if (!voterRewardInput || parseFloat(voterRewardInput) < 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setIsDAOLoading(true);
    try {
      toast.loading('Setting voter reward amount...');
      
      // Save to backend config
      const updateData = {
        voter_reward_amount: parseFloat(voterRewardInput)
      };
      
      const { data } = await api.put(API_ENDPOINTS.DEPLOYER.UPDATE_CONFIG, updateData);
      
      // Update local state
      setConfig(data.config);
      setVoterRewardAmount(data.config.voter_reward_amount?.toString() || '0');
      setVoterRewardInput('');
      
      // Log transaction
      await logDeployerAction({
        type: 'dao_set_voter_reward',
        amount: Number(voterRewardInput),
        description: `Set voter reward amount to ${voterRewardInput} LOB per vote`,
        direction: 'neutral',
        metadata: { rewardAmount: voterRewardInput }
      });
      
      toast.dismiss();
      toast.success('Voter reward amount set successfully!');
      
      // Refresh page after successful update
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.error || 'Failed to set voter reward amount');
      console.error('Error setting voter reward amount:', error);
    } finally {
      dismissWalletToasts();
      setIsDAOLoading(false);
    }
  };

  if (!isDeployerUser) {
    return (
      <div className="p-6">
        <div className="max-w-3xl mx-auto text-center bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl p-10 shadow-sm">
          <Shield className="w-12 h-12 mx-auto text-amber-500 mb-4" />
          <h1 className="text-2xl font-heading text-heading mb-2">
            Restricted Access
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            This portal is only available to the smart contract deployer.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm uppercase text-gray-500 tracking-wide mb-1">
            Workloob Blockchain Controls
          </p>
          <h1 className="text-3xl font-heading font-bold text-heading">
            Deployer Console
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Manage escrow fees, treasury balance, and deployer-only operations.
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">Contract</p>
          <p className="font-mono text-sm text-heading">
            {JOB_CONTRACT_ADDRESS.slice(0, 10)}...
            {JOB_CONTRACT_ADDRESS.slice(-6)}
          </p>
        </div>
      </div>

      {contractOwner && (
        <div
          className={`rounded-xl border p-4 ${
            ownerMismatch
              ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-200'
              : 'border-gray-200 bg-white dark:bg-gray-900 dark:border-gray-700 text-gray-700 dark:text-gray-300'
          }`}
        >
          <p className="text-sm">
            Contract owner on-chain:{' '}
            <span className="font-mono font-semibold">
              {shortenAddress?.(contractOwner) || contractOwner}
            </span>
          </p>
          {walletAddress && (
            <p className="text-xs mt-1">
              Connected wallet:{' '}
              <span className="font-mono">
                {shortenAddress?.(walletAddress) || walletAddress}
              </span>
            </p>
          )}
          {ownerMismatch && (
            <p className="text-xs mt-2 font-medium">
              Please switch to the contract owner wallet to use these controls.
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card p-4">
          <p className="text-xs uppercase text-gray-500 mb-1">Wallet status</p>
          <h3 className="text-lg font-semibold text-heading">
            {isConnected && walletAddress
              ? shortenAddress(walletAddress)
              : 'Wallet not connected'}
          </h3>
          <button
            className="btn-primary mt-3 w-full"
            onClick={connectWallet}
            disabled={isConnected}
          >
            {isConnected ? 'Wallet Connected' : 'Connect Wallet'}
          </button>
        </div>
        <div className="card p-4">
          <p className="text-xs uppercase text-gray-500 mb-1">Verification</p>
          <h3 className="text-lg font-semibold text-heading">
            {isVerified ? 'Verified deployer' : 'Verification required'}
          </h3>
          <button
            className="btn-primary mt-3 w-full"
            onClick={handleVerify}
            disabled={isVerifying || isVerified}
          >
            {isVerified ? (
              <span className="flex items-center justify-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Verified
              </span>
            ) : (
              'Verify Deployer Wallet'
            )}
          </button>
        </div>
        <div className="card p-4">
          <p className="text-xs uppercase text-gray-500 mb-1">Escrow balance (USDC)</p>
          <div className="flex items-baseline gap-2">
            <h3 className="text-2xl font-semibold text-heading">
              {balanceEth !== null ? `${balanceEth.toFixed(2)} USDC` : '--'}
            </h3>
            {balanceLoading && (
              <span className="text-xs text-gray-500">Updating…</span>
            )}
          </div>
          <p className="text-sm text-gray-500">{balanceUsd ? `$${balanceUsd}` : '--'}</p>
          <p className="text-xs text-gray-400 mt-2">
            Automatically refreshed after each action
          </p>
        </div>
      </div>

      {isVerified && (
        <>
          <div className="card p-6 space-y-6">
            <div className="flex items-center gap-3">
              <Settings className="w-5 h-5 text-accent" />
              <div>
                <h2 className="text-xl font-semibold text-heading">
                  Job Fee Controls
                </h2>
                <p className="text-sm text-gray-500">
                  Current fee:{' '}
                  {jobFeeBps !== null
                    ? `${formatPercent(jobFeeBps)}%`
                    : 'Loading...'}
                </p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Fee percentage (0% - 20%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="20"
                  value={feeInput}
                  onChange={(e) => setFeeInput(e.target.value)}
                  className="input-field"
                  placeholder="e.g. 5"
                />
              </div>
              <div className="md:w-40 flex items-end">
                <button
                  className={`btn-primary w-full ${
                    !canUseOwnerActions || isEscrowLoading ? 'opacity-60' : ''
                  }`}
                  onClick={handleUpdateFee}
                  disabled={!canUseOwnerActions || isEscrowLoading}
                >
                  {isEscrowLoading ? 'Processing...' : 'Update Fee'}
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card p-6 space-y-4">
              <div className="flex items-center gap-3">
                <PlusCircle className="w-5 h-5 text-green-500" />
                <div>
                  <h3 className="text-lg font-semibold text-heading">
                    Add Funds to Escrow
                  </h3>
                  <p className="text-sm text-gray-500">
                    Top up escrow balance (USDC)
                  </p>
                </div>
              </div>
              <input
                type="number"
                min="0"
                step="0.01"
                value={addAmount}
                onChange={(e) => setAddAmount(e.target.value)}
                className="input-field"
                placeholder="Amount in USDC"
              />
              <button
                className={`btn-primary w-full ${
                  !canUseOwnerActions || isEscrowLoading ? 'opacity-60' : ''
                }`}
                onClick={handleAddFunds}
                disabled={!canUseOwnerActions || isEscrowLoading}
              >
                {isEscrowLoading ? 'Processing...' : 'Add Funds'}
              </button>
              {!canUseOwnerActions && (
                <p className="text-xs text-amber-600">
                  Verify and connect the contract owner wallet to add funds.
                </p>
              )}
            </div>

            <div className="card p-6 space-y-4">
              <div className="flex items-center gap-3">
                <MinusCircle className="w-5 h-5 text-red-500" />
                <div>
                  <h3 className="text-lg font-semibold text-heading">
                    Withdraw Funds
                  </h3>
                  <p className="text-sm text-gray-500">
                    Transfer USDC from escrow
                  </p>
                </div>
              </div>
              <input
                type="text"
                value={withdrawAddress}
                onChange={(e) => setWithdrawAddress(e.target.value)}
                className="input-field"
                placeholder="Destination address"
              />
              <input
                type="number"
                min="0"
                step="0.01"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                className="input-field"
                placeholder="Amount in USDC"
              />
              <button
                className={`btn-primary w-full ${
                  !canUseOwnerActions || isEscrowLoading ? 'opacity-60' : ''
                }`}
                onClick={handleWithdraw}
                disabled={!canUseOwnerActions || isEscrowLoading}
              >
                {isEscrowLoading ? 'Processing...' : 'Withdraw'}
              </button>
              {!canUseOwnerActions && (
                <p className="text-xs text-amber-600">
                  Verify and connect the contract owner wallet to withdraw funds.
                </p>
              )}
            </div>
          </div>

          {/* Staking Pool Management */}
          <div className="card p-6">
            <div className="flex items-center gap-3 mb-6">
              <Lock className="w-5 h-5 text-accent" />
              <div>
                <h3 className="text-lg font-semibold text-heading">
                  Staking Pool Management
                </h3>
                <p className="text-sm text-gray-500">
                  Manage the staking reward pool
                </p>
              </div>
            </div>

            {/* Pool Info */}
            {poolInfo && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">Pool Balance</p>
                  <p className="text-lg font-semibold text-heading">
                    {parseFloat(poolInfo.totalPoolBalance || 0).toLocaleString()} LOB
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">Reward Rate (per second)</p>
                  <p className="text-lg font-semibold text-heading">
                    {parseFloat(poolInfo.currentRewardRate || 0).toFixed(4)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    APY: {poolInfo && parseFloat(poolInfo.totalStaked || 0) > 0 && parseFloat(poolInfo.apy || 0) > 0
                      ? `${parseFloat(poolInfo.apy || 0).toFixed(2)}%` 
                      : poolInfo && parseFloat(poolInfo.currentRewardRate || 0) > 0
                      ? 'Stake to see APY'
                      : 'N/A'}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">Your LOB Balance</p>
                  <p className="text-lg font-semibold text-heading">
                    {parseFloat(lobTokenBalance || 0).toLocaleString()} LOB
                  </p>
                </div>
              </div>
            )}

            {/* Fund Pool */}
            <div className="space-y-4 mb-6">
              <div className="card p-4 bg-blue-50 dark:bg-blue-900/20">
                <div className="flex items-center gap-3 mb-4">
                  <PlusCircle className="w-5 h-5 text-blue-500" />
                  <h4 className="font-semibold text-heading">Fund Pool</h4>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Fund the staking pool with reward tokens and set the duration. This sets the reward rate and APY.
                </p>
                <div className="space-y-3">
                  <input
                    type="number"
                    value={poolRewardAmount}
                    onChange={(e) => setPoolRewardAmount(e.target.value)}
                    className="input-field"
                    placeholder="Reward amount (LOB)"
                  />
                  <input
                    type="number"
                    value={poolDuration}
                    onChange={(e) => setPoolDuration(e.target.value)}
                    className="input-field"
                    placeholder="Duration (days)"
                  />
                  <button
                    onClick={handleFundPool}
                    disabled={!canUseOwnerActions || isPoolLoading || !poolRewardAmount || !poolDuration}
                    className="btn-primary w-full disabled:opacity-50"
                  >
                    {isPoolLoading ? 'Funding...' : 'Fund Pool'}
                  </button>
                </div>
              </div>

              {/* Add to Pool */}
              <div className="card p-4 bg-green-50 dark:bg-green-900/20">
                <div className="flex items-center gap-3 mb-4">
                  <PlusCircle className="w-5 h-5 text-green-500" />
                  <h4 className="font-semibold text-heading">Add to Pool</h4>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Add more tokens to the existing reward pool to increase rewards.
                </p>
                <div className="space-y-3">
                  <input
                    type="number"
                    value={poolAddAmount}
                    onChange={(e) => setPoolAddAmount(e.target.value)}
                    className="input-field"
                    placeholder="Amount to add (LOB)"
                  />
                  <button
                    onClick={handleAddToPool}
                    disabled={!canUseOwnerActions || isPoolLoading || !poolAddAmount}
                    className="btn-primary w-full disabled:opacity-50"
                  >
                    {isPoolLoading ? 'Adding...' : 'Add to Pool'}
                  </button>
                </div>
              </div>

              {/* Withdraw from Pool */}
              <div className="card p-4 bg-red-50 dark:bg-red-900/20">
                <div className="flex items-center gap-3 mb-4">
                  <MinusCircle className="w-5 h-5 text-red-500" />
                  <h4 className="font-semibold text-heading">Withdraw from Pool</h4>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Withdraw tokens from the reward pool to regulate the reward rate.
                </p>
                <div className="space-y-3">
                  <input
                    type="number"
                    value={poolWithdrawAmount}
                    onChange={(e) => setPoolWithdrawAmount(e.target.value)}
                    className="input-field"
                    placeholder="Amount to withdraw (LOB)"
                  />
                  <button
                    onClick={handleWithdrawFromPool}
                    disabled={!canUseOwnerActions || isPoolLoading || !poolWithdrawAmount}
                    className="btn-primary w-full disabled:opacity-50"
                  >
                    {isPoolLoading ? 'Withdrawing...' : 'Withdraw from Pool'}
                  </button>
                </div>
              </div>
            </div>

            {!canUseOwnerActions && (
              <p className="text-xs text-amber-600 mt-4">
                Verify and connect the contract owner wallet to manage the staking pool.
              </p>
            )}
          </div>

          {/* DAO Pool Management */}
          <div className="card p-6">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-5 h-5 text-accent" />
              <div>
                <h3 className="text-lg font-semibold text-heading">
                  DAO Pool Management
                </h3>
                <p className="text-sm text-gray-500">
                  Manage USDC and LOB token pools for governance rewards
                </p>
              </div>
            </div>

            {/* Pool Balances — always read from Base mainnet */}
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-gray-500">
                Balances from WorkLobDAO on Base mainnet (via server, with on-chain fallback)
              </p>
              <button
                type="button"
                onClick={refreshDAOPoolBalances}
                disabled={daoPoolBalanceLoading}
                className="text-xs text-accent hover:underline disabled:opacity-50"
              >
                {daoPoolBalanceLoading ? 'Refreshing…' : 'Refresh balances'}
              </button>
            </div>
            {daoPoolBalanceError && (
              <p className="text-sm text-amber-600 dark:text-amber-400 mb-3">
                {daoPoolBalanceError}
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">USDC Pool Balance</p>
                <p className="text-lg font-semibold text-heading">
                  {daoPoolBalanceLoading
                    ? '…'
                    : `${parseFloat(daoETHPoolBalance || 0).toFixed(4)} USDC`}
                </p>
                {ethPriceUsd && !daoPoolBalanceLoading && (
                  <p className="text-sm text-gray-500 mt-1">
                    ${(parseFloat(daoETHPoolBalance || 0) * ethPriceUsd).toFixed(2)} USD
                  </p>
                )}
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">LOB Token Pool Balance</p>
                <p className="text-lg font-semibold text-heading">
                  {daoPoolBalanceLoading
                    ? '…'
                    : `${parseFloat(daoLOBTokenPoolBalance || 0).toLocaleString()} LOB`}
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Voter Reward Amount</p>
                <p className="text-lg font-semibold text-heading">
                  {parseFloat(voterRewardAmount || 0).toLocaleString()} LOB
                </p>
                <p className="text-xs text-gray-500 mt-1">Per vote</p>
              </div>
            </div>

            {/* USDC Pool Management */}
            <div className="space-y-4 mb-6">
              <div className="card p-4 bg-blue-50 dark:bg-blue-900/20">
                <div className="flex items-center gap-3 mb-4">
                  <PlusCircle className="w-5 h-5 text-blue-500" />
                  <h4 className="font-semibold text-heading">Fund USDC Pool</h4>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Add USDC to the DAO pool for dispute resolution payouts.
                </p>
                <div className="space-y-3">
                  <input
                    type="number"
                    min="0"
                    step="0.0001"
                    value={daoETHFundAmount}
                    onChange={(e) => setDaoETHFundAmount(e.target.value)}
                    className="input-field"
                    placeholder="Amount in USDC"
                  />
                  {daoETHFundAmount && ethPriceUsd && (
                    <p className="text-sm text-gray-500">
                      ≈ ${(parseFloat(daoETHFundAmount || 0) * ethPriceUsd).toFixed(2)} USD
                    </p>
                  )}
                  <button
                    onClick={handleFundETHPool}
                    disabled={!canUseOwnerActions || isDAOLoading || !daoETHFundAmount}
                    className="btn-primary w-full disabled:opacity-50"
                  >
                    {isDAOLoading ? 'Funding...' : 'Fund USDC Pool'}
                  </button>
                </div>
              </div>

              <div className="card p-4 bg-red-50 dark:bg-red-900/20">
                <div className="flex items-center gap-3 mb-4">
                  <MinusCircle className="w-5 h-5 text-red-500" />
                  <h4 className="font-semibold text-heading">Withdraw from USDC Pool</h4>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Withdraw USDC from the DAO pool.
                </p>
                <div className="space-y-3">
                  <input
                    type="text"
                    value={daoETHWithdrawAddress}
                    onChange={(e) => setDaoETHWithdrawAddress(e.target.value)}
                    className="input-field"
                    placeholder="Destination address"
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.0001"
                    value={daoETHWithdrawAmount}
                    onChange={(e) => setDaoETHWithdrawAmount(e.target.value)}
                    className="input-field"
                    placeholder="Amount in USDC"
                  />
                  {daoETHWithdrawAmount && ethPriceUsd && (
                    <p className="text-sm text-gray-500">
                      ≈ ${(parseFloat(daoETHWithdrawAmount || 0) * ethPriceUsd).toFixed(2)} USD
                    </p>
                  )}
                  <button
                    onClick={handleWithdrawETHPool}
                    disabled={!canUseOwnerActions || isDAOLoading || !daoETHWithdrawAmount || !daoETHWithdrawAddress}
                    className="btn-primary w-full disabled:opacity-50"
                  >
                    {isDAOLoading ? 'Withdrawing...' : 'Withdraw from USDC Pool'}
                  </button>
                </div>
              </div>
            </div>

            {/* LOB Token Pool Management */}
            <div className="space-y-4 mb-6">
              <div className="card p-4 bg-green-50 dark:bg-green-900/20">
                <div className="flex items-center gap-3 mb-4">
                  <PlusCircle className="w-5 h-5 text-green-500" />
                  <h4 className="font-semibold text-heading">Fund LOB Token Pool</h4>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Add LOB tokens to the DAO pool for voter, referral, and blog rewards.
                </p>
                <div className="space-y-3">
                  <input
                    type="number"
                    min="0"
                    step="0.0001"
                    value={daoLOBTokenFundAmount}
                    onChange={(e) => setDaoLOBTokenFundAmount(e.target.value)}
                    className="input-field"
                    placeholder="Amount in LOB"
                  />
                  <button
                    onClick={handleFundLOBTokenPool}
                    disabled={!canUseOwnerActions || isDAOLoading || !daoLOBTokenFundAmount}
                    className="btn-primary w-full disabled:opacity-50"
                  >
                    {isDAOLoading ? 'Funding...' : 'Fund LOB Token Pool'}
                  </button>
                </div>
              </div>

              <div className="card p-4 bg-red-50 dark:bg-red-900/20">
                <div className="flex items-center gap-3 mb-4">
                  <MinusCircle className="w-5 h-5 text-red-500" />
                  <h4 className="font-semibold text-heading">Withdraw from LOB Token Pool</h4>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Withdraw LOB tokens from the DAO pool.
                </p>
                <div className="space-y-3">
                  <input
                    type="text"
                    value={daoLOBTokenWithdrawAddress}
                    onChange={(e) => setDaoLOBTokenWithdrawAddress(e.target.value)}
                    className="input-field"
                    placeholder="Destination address"
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.0001"
                    value={daoLOBTokenWithdrawAmount}
                    onChange={(e) => setDaoLOBTokenWithdrawAmount(e.target.value)}
                    className="input-field"
                    placeholder="Amount in LOB"
                  />
                  <button
                    onClick={handleWithdrawLOBTokenPool}
                    disabled={!canUseOwnerActions || isDAOLoading || !daoLOBTokenWithdrawAmount || !daoLOBTokenWithdrawAddress}
                    className="btn-primary w-full disabled:opacity-50"
                  >
                    {isDAOLoading ? 'Withdrawing...' : 'Withdraw from LOB Token Pool'}
                  </button>
                </div>
              </div>
            </div>

            {/* Voter Reward Settings */}
            <div className="card p-4 bg-purple-50 dark:bg-purple-900/20">
              <div className="flex items-center gap-3 mb-4">
                <Settings className="w-5 h-5 text-purple-500" />
                <h4 className="font-semibold text-heading">Set Voter Reward Amount</h4>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Set the amount of LOB tokens voters earn per vote. Current: {parseFloat(voterRewardAmount || 0).toLocaleString()} LOB
              </p>
              <div className="space-y-3">
                <input
                  type="number"
                  min="0"
                  step="0.0001"
                  value={voterRewardInput}
                  onChange={(e) => setVoterRewardInput(e.target.value)}
                  className="input-field"
                  placeholder="Reward amount in LOB"
                />
                <button
                  onClick={handleSetVoterRewardAmount}
                  disabled={!isDeployerUser || isDAOLoading || !voterRewardInput}
                  className="btn-primary w-full disabled:opacity-50"
                >
                  {isDAOLoading ? 'Setting...' : 'Set Voter Reward Amount'}
                </button>
              </div>
            </div>

            {!canUseOwnerActions && (
              <p className="text-xs text-amber-600 mt-4">
                Verify and connect the contract owner wallet to manage the DAO pools.
              </p>
            )}
          </div>

          {/* Configuration Management */}
          {isDeployerUser && (
            <div className="card p-6">
              <div className="flex items-center gap-3 mb-6">
                <Settings className="w-6 h-6 text-accent" />
                <div>
                  <h3 className="text-xl font-semibold text-heading">
                    Platform Configuration
                  </h3>
                  <p className="text-sm text-gray-500">
                    Manage activity points and governance requirements
                  </p>
                </div>
              </div>

              {isConfigLoading ? (
                <div className="flex items-center justify-center py-10">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Activity Points for Job/Gig Completion */}
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <label className="block text-sm font-medium text-heading mb-2">
                      Activity Points for Job/Gig Completion
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Points awarded to both client and talent when a job or gig is completed
                    </p>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        min="0"
                        value={configInputs.activity_points_job_completion}
                        onChange={(e) => setConfigInputs(prev => ({
                          ...prev,
                          activity_points_job_completion: e.target.value
                        }))}
                        className="input-field flex-1"
                        placeholder="10"
                      />
                      <span className="text-sm text-gray-500">Current: {config.activity_points_job_completion}</span>
                    </div>
                  </div>

                  {/* Activity Points for Voting */}
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <label className="block text-sm font-medium text-heading mb-2">
                      Activity Points for Voting
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Points awarded to users when they vote on governance proposals
                    </p>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        min="0"
                        value={configInputs.activity_points_voting}
                        onChange={(e) => setConfigInputs(prev => ({
                          ...prev,
                          activity_points_voting: e.target.value
                        }))}
                        className="input-field flex-1"
                        placeholder="5"
                      />
                      <span className="text-sm text-gray-500">Current: {config.activity_points_voting}</span>
                    </div>
                  </div>

                  {/* Minimum Locked Staking for Governance */}
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <label className="block text-sm font-medium text-heading mb-2">
                      Minimum Locked Staking for Governance
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Minimum LOB tokens that must be locked in staking to participate in governance voting
                    </p>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        min="0"
                        value={configInputs.min_locked_staking_governance}
                        onChange={(e) => setConfigInputs(prev => ({
                          ...prev,
                          min_locked_staking_governance: e.target.value
                        }))}
                        className="input-field flex-1"
                        placeholder="100"
                      />
                      <span className="text-sm text-gray-500">Current: {config.min_locked_staking_governance} LOB</span>
                    </div>
                  </div>

                  {/* Minimum Activity Points for Governance */}
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <label className="block text-sm font-medium text-heading mb-2">
                      Minimum Activity Points for Governance
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Minimum activity points required to vote or resolve proposals in governance
                    </p>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        min="0"
                        value={configInputs.min_activity_points_governance}
                        onChange={(e) => setConfigInputs(prev => ({
                          ...prev,
                          min_activity_points_governance: e.target.value
                        }))}
                        className="input-field flex-1"
                        placeholder="20"
                      />
                      <span className="text-sm text-gray-500">Current: {config.min_activity_points_governance}</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-heading mb-2">
                      Settlement Percentage (%)
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={configInputs.settlement_percentage}
                        onChange={(e) => setConfigInputs(prev => ({
                          ...prev,
                          settlement_percentage: e.target.value
                        }))}
                        className="input-field flex-1"
                        placeholder="90"
                      />
                      <span className="text-sm text-gray-500">Current: {config.settlement_percentage}%</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Percentage of remaining escrow amount that can be split in settlement (default: 90%)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-heading mb-2">
                      Voting Duration (Days)
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        step="1"
                        min="1"
                        value={configInputs.voting_duration_days}
                        onChange={(e) => setConfigInputs(prev => ({
                          ...prev,
                          voting_duration_days: e.target.value
                        }))}
                        className="input-field flex-1"
                        placeholder="5"
                      />
                      <span className="text-sm text-gray-500">Current: {config.voting_duration_days} days</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Number of days for governance voting period (default: 5 days)
                    </p>
                  </div>

                  {/* Blog Earnings Configuration */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
                    <h4 className="text-lg font-semibold text-heading mb-4">Blog Earnings Configuration</h4>
                    
                    <div className="space-y-4">
                      {/* Views Rate */}
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <label className="block text-sm font-medium text-heading mb-2">
                          LOB Tokens per Views Threshold
                        </label>
                        <p className="text-xs text-gray-500 mb-3">
                          Amount of LOB tokens earned when blog reaches the views threshold
                        </p>
                        <div className="flex items-center gap-3">
                          <input
                            type="number"
                            min="0"
                            value={configInputs.blog_earnings_views_rate}
                            onChange={(e) => setConfigInputs(prev => ({
                              ...prev,
                              blog_earnings_views_rate: e.target.value
                            }))}
                            className="input-field flex-1"
                            placeholder="100"
                          />
                          <span className="text-sm text-gray-500">Current: {config.blog_earnings_views_rate} LOB</span>
                        </div>
                      </div>

                      {/* Views Threshold */}
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <label className="block text-sm font-medium text-heading mb-2">
                          Views Threshold
                        </label>
                        <p className="text-xs text-gray-500 mb-3">
                          Number of views required to earn the LOB tokens
                        </p>
                        <div className="flex items-center gap-3">
                          <input
                            type="number"
                            min="1"
                            value={configInputs.blog_earnings_views_threshold}
                            onChange={(e) => setConfigInputs(prev => ({
                              ...prev,
                              blog_earnings_views_threshold: e.target.value
                            }))}
                            className="input-field flex-1"
                            placeholder="1000"
                          />
                          <span className="text-sm text-gray-500">Current: {config.blog_earnings_views_threshold} views</span>
                        </div>
                      </div>

                      {/* Impressions Rate */}
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <label className="block text-sm font-medium text-heading mb-2">
                          LOB Tokens per Impressions Threshold
                        </label>
                        <p className="text-xs text-gray-500 mb-3">
                          Amount of LOB tokens earned when blog reaches the impressions threshold
                        </p>
                        <div className="flex items-center gap-3">
                          <input
                            type="number"
                            min="0"
                            value={configInputs.blog_earnings_impressions_rate}
                            onChange={(e) => setConfigInputs(prev => ({
                              ...prev,
                              blog_earnings_impressions_rate: e.target.value
                            }))}
                            className="input-field flex-1"
                            placeholder="100"
                          />
                          <span className="text-sm text-gray-500">Current: {config.blog_earnings_impressions_rate} LOB</span>
                        </div>
                      </div>

                      {/* Impressions Threshold */}
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <label className="block text-sm font-medium text-heading mb-2">
                          Impressions Threshold
                        </label>
                        <p className="text-xs text-gray-500 mb-3">
                          Number of impressions required to earn the LOB tokens
                        </p>
                        <div className="flex items-center gap-3">
                          <input
                            type="number"
                            min="1"
                            value={configInputs.blog_earnings_impressions_threshold}
                            onChange={(e) => setConfigInputs(prev => ({
                              ...prev,
                              blog_earnings_impressions_threshold: e.target.value
                            }))}
                            className="input-field flex-1"
                            placeholder="100"
                          />
                          <span className="text-sm text-gray-500">Current: {config.blog_earnings_impressions_threshold} impressions</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleUpdateConfig}
                    disabled={isUpdatingConfig}
                    className="btn-primary w-full"
                  >
                    {isUpdatingConfig ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Updating...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Update Configuration
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="card p-6">
            <div className="flex items-center gap-3 mb-4">
              <List className="w-5 h-5 text-accent" />
              <div>
                <h3 className="text-lg font-semibold text-heading">
                  Transaction History
                </h3>
                <p className="text-sm text-gray-500">
                  Logged deployer-only blockchain actions
                </p>
                {loggingDisabled && (
                  <p className="text-xs text-amber-600 mt-1">
                    Backend logging disabled. Configure DEPLOYER_WALLET_ADDRESS on the server to
                    store deployer activity.
                  </p>
                )}
              </div>
            </div>
            {transactionsLoading ? (
              <div className="flex items-center justify-center py-10">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
              </div>
            ) : transactionsData?.transactions?.length ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
                  <thead>
                    <tr className="text-left text-xs font-semibold text-gray-500 uppercase">
                      <th className="px-4 py-3">Type</th>
                      <th className="px-4 py-3">Description</th>
                      <th className="px-4 py-3">Amount</th>
                      <th className="px-4 py-3">Timestamp</th>
                      <th className="px-4 py-3">Tx</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactionsData.transactions.map((tx) => (
                      <TransactionRow key={tx._id} tx={tx} />
                    ))}
                  </tbody>
                </table>
                <div className="flex items-center justify-between mt-4">
                  <button
                    className="btn-outline"
                    disabled={transactionsPage === 1}
                    onClick={() =>
                      setTransactionsPage((prev) => Math.max(prev - 1, 1))
                    }
                  >
                    Previous
                  </button>
                  <p className="text-sm text-gray-500">
                    Page {transactionsData.page} of {transactionsData.pages || 1}
                  </p>
                  <button
                    className="btn-outline"
                    disabled={
                      transactionsData.page >= (transactionsData.pages || 1)
                    }
                    onClick={() =>
                      setTransactionsPage((prev) => prev + 1)
                    }
                  >
                    Next
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
                <div className="text-center space-y-2">
                  <Wallet className="w-10 h-10 mx-auto text-accent" />
                  <p>No deployer transactions recorded yet.</p>
                </div>
              </div>
            )}
          </div>
        </>
      )}


      <div className="card p-6 flex gap-4 items-start bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
        <AlertTriangle className="w-6 h-6 text-amber-600 mt-1" />
        <div>
          <h3 className="text-lg font-semibold text-heading mb-1">
            Security Notice
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Always ensure you are connected with the official deployer wallet.
            All actions are recorded on-chain and mirrored in the Workloob
            backend for audit purposes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DeployerPortalPage;

